import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import { logout } from './store/slices/authSlice';
import { isTokenValid, registerSessionExpiredHandler } from './utils/apiUtils';
import { NotificationProvider, useNotification } from './utils/NotificationContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AccountDashboard from './pages/AccountDashboard';
import MicroSites from './pages/Account/MicroSites';
import ProtectedRoute from './components/ProtectedRoute';
import MicroSiteOverview from './pages/MicroSiteOverview';
import MicroSitePages from './pages/MicroSitePages';
import PublicPage from './pages/public/PublicPage';
import AIUsageDashboard from './pages/Account/AIUsageDashboard';
import AIMediaGeneration from './pages/Account/AIImageGeneration';
import AIContentGeneration from './pages/Account/AIContentGeneration';
import MicroSiteDetails from './pages/Account/MicroSiteDetails';
import PageBuilder from './pages/MicroSite/PageBuilder';
import MicroSitePage from './pages/public/MicroSitePage';
import AnalyticsDashboard from './pages/MicroSite/Analytics/Dashboard';
import ProductsInventory from './pages/Account/ProductsInventory';

//original main color: #1976d2
//original secondary color: #dc004e

const theme = createTheme({
  palette: {
    primary: {
      main: '#5c2d91',
    },
    secondary: {
      main: '#19ba8b',
    },
  },
});

// Inner App component that can use the notification context
function AppContent() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const isSubdomain = window.location.hostname.split('.').length > 2;

  // Register the session expired notification handler
  useEffect(() => {
    registerSessionExpiredHandler(() => {
      showNotification('Your session has expired. Please log in again.', 'warning');
    });
  }, [showNotification]);

  // Check token validity on app startup
  useEffect(() => {
    // If token exists but is invalid, log the user out
    if (localStorage.getItem('token') && !isTokenValid()) {
      console.log('Invalid or expired token detected on startup, logging out');
      dispatch(logout());
      showNotification('Your session has expired. Please log in again.', 'warning');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }, [dispatch, showNotification]);

  // If accessing via subdomain, only show the PublicPage component
  if (isSubdomain) {
    const path = location.pathname;
    const slug = path === '/' ? 'home' : path.slice(1); // Remove leading slash

    console.log('Subdomain routing:', {
      path,
      slug,
      hostname: window.location.hostname
    });

    return (
      <>
        <Routes>
          <Route path="/*" element={<PublicPage />} />
        </Routes>
      </>
    );
  }

  // Regular app routes
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/dashboard"
          element={
            <ProtectedRoute>
              <AccountDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/products/*"
          element={
            <ProtectedRoute>
              <ProductsInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/sites"
          element={
            <ProtectedRoute>
              <MicroSites />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/sites/:siteId"
          element={
            <ProtectedRoute>
              <MicroSiteOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/sites/:siteId/pages"
          element={
            <ProtectedRoute>
              <MicroSitePages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/ai-usage"
          element={
            <ProtectedRoute>
              <AIUsageDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/ai-media"
          element={
            <ProtectedRoute>
              <AIMediaGeneration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId/ai-content"
          element={
            <ProtectedRoute>
              <AIContentGeneration />
            </ProtectedRoute>
          }
        />
        <Route path="/sites/:subdomain/:slug" element={<MicroSitePage />} />
        <Route path="/:subdomain/:slug" element={<PublicPage />} />
        <Route path="/account/microsites/:microSiteId" element={<MicroSiteDetails />} />
        <Route path="/account/:accountId/microsites/:microSiteId/pages/:pageId/edit" element={<PageBuilder />} />
        <Route path="/account/microsites/:microSiteId/pages/:pageId/edit" element={<PageBuilder />} />
        <Route path="/sites/:subdomain" element={<MicroSitePage />} />
        <Route path="/sites/:subdomain/pages/:slug" element={<MicroSitePage />} />
        <Route path="/account/:accountId/microsites/:microSiteId/analytics" element={
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } />
      </Routes>
    </>
  );
}

// Main App component that provides the notification context
function App() {
  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
